import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ShieldCheck } from "lucide-react";
import BrandLogo from "./BrandLogo";
import FooterTurismoIntelligente from "./footer/FooterTurismoIntelligente";
import FooterGemellaggi from "./footer/FooterGemellaggi";
import FooterSezionePartner from "./footer/FooterSezionePartner";
import { useLang } from "../LangContext";
import "../footer-legal-bar.css";
import "../footer-main-grid.css";

/**
 * Route map (verified in App.js) — paste brochure paths → real routes:
 * /piazza → /piazze · /tour → /benvenuto · /mappa → /mappa-del-mondo
 * /censimento → /censisci-luogo · /accesso → /richiedi-accesso
 * /comuni-amici, /gemellaggi, /manifesto, /eventi — as-is
 */
const NAV_PRIMARY = [
  { to: "/piazze", labelKey: "nav.piazze", fallback: "Piazza Social" },
  { to: "/eventi", labelKey: "nav.events", fallback: "Eventi & Sagre" },
  { to: "/benvenuto", labelKey: "footer.welcome_tour", fallback: "Tour di benvenuto" },
];

const NAV_DISCOVER = [
  { to: "/mappa-del-mondo", labelKey: "footer.map", fallback: "Mappa dei borghi" },
  { to: "/gemellaggi", labelKey: "nav.twinnings", fallback: "Gemellaggi" },
  { to: "/comuni-amici", labelKey: "footer.comuni", fallback: "Comuni amici" },
  { to: "/mondo", labelKey: "nav.world", fallback: "Mondo" },
];

const NAV_COMMUNITY = [
  { to: "/manifesto", labelKey: "footer.manifesto", fallback: "Manifesto" },
  { to: "/censisci-luogo", labelKey: "footer.census", fallback: "Censisci un luogo" },
  { to: "/richiedi-accesso", labelKey: "footer.request_access", fallback: "Richiedi un accesso" },
];

const AREA_ISTITUZIONALE = [
  { to: "/governance", label: "Governance" },
  { to: "/centrale-comando", label: "Progetti del territorio" },
  { to: "/ecosistema-governance", label: "Rete dei borghi" },
  { to: "/stanza-sindaci", label: "Stanza dei Sindaci" },
  { to: "/storia-protocollo", label: "Storia del progetto" },
  { to: "/richiedi-accesso?kind=comune", label: "Gestione Comune" },
  { to: "/richiedi-accesso?kind=proloco", label: "Pannello Pro Loco" },
  { to: "/pro-loco", label: "Pro Loco d'Italia" },
  { to: "/qr", label: "Guide QR" },
  { to: "/ufficio-stampa", label: "Ufficio stampa" },
  { to: "/registro-innovazione", label: "Storico dal 2013" },
];

const LegalLink = ({ to, testid, children }) => (
  <Link
    to={to}
    data-testid={testid}
    className="text-ink2 hover:text-terracotta transition-colors normal-case tracking-normal"
  >
    {children}
  </Link>
);

function NavColumn({ id, title, items, t }) {
  return (
    <nav className="footer-main__col" aria-labelledby={id}>
      <h5 id={id} className="footer-main__heading">
        {title}
      </h5>
      <ul className="footer-main__list">
        {items.map((item) => (
          <li key={item.to}>
            <Link to={item.to}>{t(item.labelKey) || item.fallback}</Link>
          </li>
        ))}
      </ul>
    </nav>
  );
}

/**
 * Footer istituzionale brochure — montato una sola volta da App via `Footer`.
 * Accordion: Area Istituzionale + Turismo QR/AR + Gemellaggi + Partner (componenti dedicati).
 */
function FooterIstituzionale() {
  const { t, lang } = useLang();
  const [areaOpen, setAreaOpen] = useState(false);

  useEffect(() => {
    if (!areaOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setAreaOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [areaOpen]);

  return (
    <footer
      className="site-footer border-t border-borderline bg-parchment2/40 paper-texture"
      data-testid="site-footer"
      lang={lang}
    >
      <div className="footer-main max-w-[1400px] mx-auto px-5 md:px-10">
        <div className="footer-main__grid" data-testid="footer-main-grid">
          <div className="footer-main__col footer-main__col--brand">
            <div className="footer-main__logo mb-3">
              <BrandLogo />
            </div>
            <p className="footer-main__blurb text-ink2" data-testid="footer-site-description">
              {t("footer.blurb")}
            </p>
            <Link
              to="/privacy"
              data-testid="footer-trust-badge"
              className="footer-main__badge inline-flex items-center gap-2 text-[11px] font-mono tracking-[0.15em] uppercase text-ink2 border border-borderline bg-white/70 px-3 py-1.5 rounded-full hover:border-terracotta/50 hover:text-terracotta transition-colors"
              title={t("footer.trust")}
            >
              <ShieldCheck size={14} className="text-terracotta shrink-0" aria-hidden />
              <span>{t("footer.trust")}</span>
            </Link>
          </div>

          <NavColumn
            id="footer-nav-heading"
            title={t("footer.nav_title")}
            items={NAV_PRIMARY}
            t={t}
          />
          <NavColumn
            id="footer-discover-heading"
            title={t("footer.discover_title")}
            items={NAV_DISCOVER}
            t={t}
          />
          <NavColumn
            id="footer-community-heading"
            title={t("footer.community_title")}
            items={NAV_COMMUNITY}
            t={t}
          />
        </div>
      </div>

      <div className="border-t border-borderline bg-parchment/50" data-testid="footer-area-istituzionale">
        <button
          type="button"
          onClick={() => setAreaOpen((v) => !v)}
          aria-expanded={areaOpen}
          aria-controls="footer-area-istituzionale-panel"
          className="w-full max-w-[1400px] mx-auto px-5 md:px-10 py-4 flex items-center justify-between gap-4 text-left hover:bg-parchment2/40 transition-colors"
          data-testid="footer-area-istituzionale-toggle"
        >
          <div className="min-w-0">
            <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-terracotta mb-1">
              {t("footer.institutional")}
            </p>
            <p className="font-display text-lg sm:text-xl text-ink leading-snug">
              {t("footer.institutional_lead")}
            </p>
          </div>
          <span
            className={`shrink-0 flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider text-ink2 ${
              areaOpen ? "text-terracotta" : ""
            }`}
          >
            {areaOpen ? t("footer.close") : t("footer.open")}
            <ChevronDown
              size={18}
              className={`transition-transform duration-200 ${areaOpen ? "rotate-180" : ""}`}
              aria-hidden
            />
          </span>
        </button>
        {areaOpen && (
          <div
            id="footer-area-istituzionale-panel"
            className="border-t border-borderline/80"
            data-testid="footer-area-istituzionale-panel"
          >
            <ul
              className="max-w-[1400px] mx-auto px-5 md:px-10 py-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-2 text-sm text-ink list-none m-0"
              data-testid="footer-tech-nav"
            >
              {AREA_ISTITUZIONALE.map((item) => (
                <li key={item.to}>
                  <Link to={item.to} className="hover:text-terracotta">
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <FooterTurismoIntelligente />
      <FooterGemellaggi />
      <FooterSezionePartner />


      <div className="border-t border-borderline py-4 px-5 bg-parchment/60">
        <div className="footer-legal-bar-scroll max-w-[1400px] mx-auto">
          <p className="footer-legal-bar" data-testid="footer-legal-bar" aria-label="Copyright e link legali">
            <span>© 2013-26 6AMICO - COMMUNITY DEI BORGHI</span>
            <span className="footer-legal-bar__sep" aria-hidden>
              |
            </span>
            <LegalLink to="/privacy" testid="footer-privacy">
              Privacy &amp; GDPR
            </LegalLink>
            <span className="footer-legal-bar__sep" aria-hidden>
              |
            </span>
            <LegalLink to="/cookie-policy" testid="footer-cookie-policy">
              Cookie Policy
            </LegalLink>
            <span className="footer-legal-bar__sep" aria-hidden>
              |
            </span>
            <LegalLink to="/termini" testid="footer-termini">
              Termini d&apos;Uso
            </LegalLink>
            <span className="footer-legal-bar__sep" aria-hidden>
              |
            </span>
            <LegalLink to="/dati-societari" testid="footer-dati-societari">
              Dati Societari
            </LegalLink>
            <span className="footer-legal-bar__sep" aria-hidden>
              |
            </span>
            <span>Privacy · GDPR · DSA · AI Act</span>
          </p>
        </div>
      </div>
    </footer>
  );
}

export default FooterIstituzionale;
export { FooterIstituzionale };
